import { Link, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { ArrowRight, ArrowLeft, Clock, Linkedin, Twitter, Link2, Check } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import useSeo, { SITE } from "@/lib/useSeo";
import MarketingShell from "@/components/marketing/MarketingShell";
import { RESOURCES, getResource } from "./resourcesData";

const mdLink = ({ node, href, children, ...props }) => {
  const url = href || "";
  const cls = "font-semibold text-accent-700 underline underline-offset-2 hover:text-accent-600 transition-colors";
  if (url.startsWith("https://")) return <a href={url} target="_blank" rel="noopener noreferrer" className={cls} {...props}>{children}</a>;
  if (url.startsWith("/")) return <Link to={url} className={cls} {...props}>{children}</Link>;
  return <a href={url} className={cls} {...props}>{children}</a>;
};

// Verbatim markdown rendering, styled to match the structured-article typography and site tokens.
const MD = {
  h1: ({ node, ...p }) => <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mt-12" {...p} />,
  h2: ({ node, ...p }) => <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mt-12" {...p} />,
  h3: ({ node, ...p }) => <h3 className="font-head text-xl font-extrabold tracking-tight mt-10" {...p} />,
  p: ({ node, ...p }) => <p className="text-body leading-relaxed mt-5" {...p} />,
  ul: ({ node, ...p }) => <ul className="mt-5 space-y-2 list-disc pl-5 text-body marker:text-accent-500" {...p} />,
  ol: ({ node, ...p }) => <ol className="mt-5 space-y-2 list-decimal pl-5 text-body marker:font-bold marker:text-accent-700" {...p} />,
  li: ({ node, ...p }) => <li className="leading-relaxed pl-1" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-bold text-foreground" {...p} />,
  em: ({ node, ...p }) => <em className="italic" {...p} />,
  a: mdLink,
  blockquote: ({ node, ...p }) => <blockquote className="mt-10 border-l-4 border-accent-400 bg-accent-50 rounded-r-xl px-5 py-4 text-body font-semibold leading-relaxed" {...p} />,
  hr: () => <hr className="mt-12 border-border-soft" />,
  img: ({ node, ...p }) => <img className="w-full rounded-xl border border-border-soft my-10" loading="lazy" {...p} />,
  table: ({ node, ...p }) => (
    <div className="my-8 overflow-x-auto rounded-xl border border-border-soft">
      <table className="w-full text-sm text-left border-collapse" {...p} />
    </div>
  ),
  thead: ({ node, ...p }) => <thead className="bg-muted" {...p} />,
  th: ({ node, ...p }) => <th className="px-4 py-3 font-bold text-foreground border-b border-border-soft align-top" {...p} />,
  td: ({ node, ...p }) => <td className="px-4 py-3 text-body border-b border-border-soft align-top" {...p} />,
};

// Slim top scroll-progress indicator for long-form articles.
function ReadingProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setP(max > 0 ? Math.min(100, Math.max(0, (h.scrollTop / max) * 100)) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] h-1 bg-transparent pointer-events-none" aria-hidden="true" data-testid="reading-progress">
      <div className="h-full bg-gradient-to-r from-accent-500 to-brand-500 transition-[width] duration-150 ease-out" style={{ width: `${p}%` }} data-testid="reading-progress-bar" />
    </div>
  );
}

// One-tap share buttons (LinkedIn / X / copy link) for an article.
function ShareBar({ title, url }) {
  const [copied, setCopied] = useState(false);
  const enc = encodeURIComponent;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
  const x = `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(title)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      ta.remove();
    }
    setCopied(true);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };
  const base = "inline-flex items-center gap-2 rounded-full border border-border-soft px-4 py-2 text-sm font-semibold text-body hover:border-ink hover:bg-muted transition-colors";
  return (
    <div className="mt-14 pt-8 border-t border-border-soft" data-testid="article-share">
      <div className="pp-overline">Share this article</div>
      <div className="mt-4 flex flex-wrap gap-3">
        <a href={linkedin} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin" aria-label="Share on LinkedIn" className={base}><Linkedin size={16} /> LinkedIn</a>
        <a href={x} target="_blank" rel="noopener noreferrer" data-testid="share-x" aria-label="Share on X" className={base}><Twitter size={16} /> X</a>
        <button type="button" onClick={copy} data-testid="share-copy" aria-label="Copy link to this article" className={base}>{copied ? <Check size={16} /> : <Link2 size={16} />} {copied ? "Copied" : "Copy link"}</button>
      </div>
    </div>
  );
}

function Block({ s }) {
  if (s.type === "lede") return <p className="text-xl text-body leading-relaxed">{s.text}</p>;
  if (s.type === "h2") return <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mt-12">{s.text}</h2>;
  if (s.type === "p") return <p className="text-body leading-relaxed mt-5">{s.text}</p>;
  if (s.type === "bullets") return (
    <ul className="mt-5 space-y-3">
      {s.items.map((it) => (
        <li key={it} className="flex gap-3 text-body leading-relaxed"><span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />{it}</li>
      ))}
    </ul>
  );
  if (s.type === "callout") return (
    <blockquote className="mt-10 border-l-4 border-accent-400 bg-accent-50 rounded-r-xl px-5 py-4 text-body font-semibold leading-relaxed">{s.text}</blockquote>
  );
  if (s.type === "cta") return (
    <div className="mt-14 pp-card p-8 bg-muted">
      <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">{s.heading}</h2>
      <div className="mt-6 flex flex-wrap gap-4">
        <Link to={s.primary.to} data-testid="resource-cta-primary" className="pp-btn-dark">{s.primary.label} <ArrowRight size={16} /></Link>
        {s.secondary && <Link to={s.secondary.to} data-testid="resource-cta-secondary" className="pp-btn-ghost">{s.secondary.label}</Link>}
      </div>
    </div>
  );
  return null;
}

export default function ResourceArticle() {
  const { slug } = useParams();
  const a = getResource(slug);
  useSeo({
    title: a ? `${a.title} | ChangeOS` : "Resource not found | ChangeOS",
    description: a ? a.description : "This article could not be found.",
    path: `/resources/${slug}`,
    image: a ? `${SITE}/images/og/${a.slug}.png` : undefined,
  });
  if (!a) return (
    <MarketingShell>
      <section className="max-w-3xl mx-auto px-6 py-28 text-center" data-testid="resource-not-found">
        <h1 className="font-head text-3xl font-extrabold">We couldn't find that article</h1>
        <p className="text-subtle mt-3">It may have moved. Browse everything in resources instead.</p>
        <Link to="/resources" className="pp-btn-dark mt-6 inline-flex">Back to resources</Link>
      </section>
    </MarketingShell>
  );
  const others = RESOURCES.filter((r) => r.slug !== a.slug);
  return (
    <MarketingShell>
      <ReadingProgress />
      <article className="max-w-3xl mx-auto px-6 pt-16 pb-24" data-testid={`resource-article-${a.slug}`}>
        <Link to="/resources" data-testid="resource-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"><ArrowLeft size={15} /> Resources</Link>
        <div className="pp-overline mt-8">{a.kicker}</div>
        <h1 className="font-head font-extrabold tracking-tight text-4xl sm:text-5xl mt-3 leading-[1.08]">{a.title}</h1>
        <div className="mt-5 flex items-center gap-4 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><Clock size={14} /> {a.minutes} min read</span>
          <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-bold text-muted-foreground">{a.audience}</span>
        </div>
        <div className="mt-10">
          {a.body
            ? <div className="[&>p:first-of-type]:text-xl [&>p:first-of-type]:mt-0" data-testid="resource-markdown"><ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{a.body}</ReactMarkdown></div>
            : a.sections.map((s, i) => <Block key={i} s={s} />)}
        </div>
        <ShareBar title={a.title} url={`${SITE}/resources/${a.slug}`} />
      </article>
      {others.length > 0 && (
        <section className="border-t border-border-soft bg-muted">
          <div className="max-w-3xl mx-auto px-6 py-14">
            <div className="pp-overline">Keep reading</div>
            <div className="mt-5 space-y-3">
              {others.map((r) => (
                <Link key={r.slug} to={`/resources/${r.slug}`} data-testid={`resource-next-${r.slug}`} className="group flex items-center justify-between gap-4 pp-card p-5 hover:border-ink transition-colors">
                  <div>
                    <div className="font-mono text-[11px] text-accent-700">{r.audience}</div>
                    <div className="font-head text-lg font-extrabold mt-0.5">{r.title}</div>
                  </div>
                  <ArrowRight size={18} className="shrink-0 text-muted-foreground group-hover:text-accent-600 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </MarketingShell>
  );
}
